import { useState } from 'react';
import { FaTrophy, FaMedal, FaFire, FaStar, FaAward, FaUsers, FaChartLine, FaCalendarAlt, FaCheck, FaGamepad, FaPlay, FaTimes, FaBolt, FaGem } from 'react-icons/fa';

const Gamification = () => {
  const [activeTab, setActiveTab] = useState('games');
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameModal, setGameModal] = useState(false);
  
  // Learning Games Data
  const learningGames = [
    {
      id: 1,
      title: 'JavaScript Puzzle Master',
      description: 'Solve JavaScript coding challenges to earn XP',
      xpReward: 50,
      difficulty: 'Beginner',
      difficultyColor: 'bg-green-100 text-green-800',
      icon: <FaBolt className="text-yellow-500" />,
      completed: false,
      locked: false,
      completedCount: 0,
      totalChallenges: 10
    },
    {
      id: 2,
      title: 'React Drag & Drop Builder',
      description: 'Build React components using drag and drop interface',
      xpReward: 75,
      difficulty: 'Intermediate',
      difficultyColor: 'bg-yellow-100 text-yellow-800',
      icon: <FaGem className="text-blue-500" />,
      completed: false,
      locked: false,
      completedCount: 3,
      totalChallenges: 8
    },
    {
      id: 3,
      title: 'Debug the Code',
      description: 'Find and fix bugs in various code snippets',
      xpReward: 100,
      difficulty: 'Advanced',
      difficultyColor: 'bg-red-100 text-red-800',
      icon: <FaTrophy className="text-purple-500" />,
      completed: false,
      locked: false,
      completedCount: 1,
      totalChallenges: 12
    },
    {
      id: 4,
      title: 'Algorithm Race',
      description: 'Solve algorithmic problems against the clock',
      xpReward: 120,
      difficulty: 'Expert',
      difficultyColor: 'bg-purple-100 text-purple-800',
      icon: <FaFire className="text-orange-500" />,
      completed: false,
      locked: true,
      completedCount: 0,
      totalChallenges: 15
    }
  ];

  const userProgress = {
    totalXP: 1850,
    currentLevel: 7,
    nextLevel: 8,
    xpToNextLevel: 250,
    completedGames: ['javascript-basics', 'react-intro'],
    unlockedBadges: 6,
    totalBadges: 12,
    currentStreak: 5,
    longestStreak: 12
  };

  // Sample data for demonstration
  const badges = [
    { id: 1, name: 'First Course Completed', icon: <FaTrophy className="text-yellow-500" />, earned: true, date: '2023-10-15', description: 'Completed your first course on the platform' },
    { id: 2, name: 'Perfect Quiz Score', icon: <FaStar className="text-yellow-500" />, earned: true, date: '2023-11-02', description: 'Achieved 100% on a quiz' },
    { id: 3, name: 'Discussion Starter', icon: <FaUsers className="text-blue-500" />, earned: true, date: '2023-11-10', description: 'Started 5 discussions that received responses' },
    { id: 4, name: '7-Day Streak', icon: <FaFire className="text-orange-500" />, earned: false, progress: 5, total: 7, description: 'Log in and complete at least one activity for 7 consecutive days' },
    { id: 5, name: 'Code Master', icon: <FaAward className="text-purple-500" />, earned: false, progress: 3, total: 5, description: 'Complete 5 coding assignments with a score of 90% or higher' },
    { id: 6, name: 'Early Bird', icon: <FaCalendarAlt className="text-green-500" />, earned: false, progress: 0, total: 5, description: 'Complete 5 assignments at least 3 days before the deadline' },
  ];
  
  const leaderboards = [
    { category: 'Course Completion', users: [
      { rank: 1, name: 'Alex Johnson', points: 1250, avatar: null },
      { rank: 2, name: 'Maria Garcia', points: 1180, avatar: null },
      { rank: 3, name: 'David Kim', points: 1050, avatar: null },
      { rank: 4, name: 'Sarah Williams', points: 980, avatar: null },
      { rank: 5, name: 'Current User', points: 920, avatar: null, isCurrentUser: true },
    ]},
    { category: 'Discussion Participation', users: [
      { rank: 1, name: 'Emma Davis', points: 89, avatar: null },
      { rank: 2, name: 'Current User', points: 76, avatar: null, isCurrentUser: true },
      { rank: 3, name: 'James Wilson', points: 72, avatar: null },
      { rank: 4, name: 'Olivia Martinez', points: 65, avatar: null },
      { rank: 5, name: 'Noah Thompson', points: 58, avatar: null },
    ]},
    { category: 'Quiz Performance', users: [
      { rank: 1, name: 'Sophia Lee', points: 98, avatar: null },
      { rank: 2, name: 'Benjamin Clark', points: 96, avatar: null },
      { rank: 3, name: 'Isabella Rodriguez', points: 95, avatar: null },
      { rank: 4, name: 'Current User', points: 92, avatar: null, isCurrentUser: true },
      { rank: 5, name: 'Ethan Wright', points: 90, avatar: null },
    ]},
  ];
  
  const streakData = {
    currentStreak: 5,
    longestStreak: 12,
    thisWeek: [true, true, true, true, true, false, false], // Mon to Sun
    nextMilestone: 7,
    streakHistory: [
      { date: '2023-11-01', completed: true },
      { date: '2023-11-02', completed: true },
      { date: '2023-11-03', completed: true },
      { date: '2023-11-04', completed: false },
      { date: '2023-11-05', completed: false },
      { date: '2023-11-06', completed: true },
      { date: '2023-11-07', completed: true },
      { date: '2023-11-08', completed: true },
      { date: '2023-11-09', completed: true },
      { date: '2023-11-10', completed: true },
    ]
  };
  
  const pointsData = {
    totalPoints: 1850,
    level: 7,
    nextLevel: 8,
    pointsToNextLevel: 250,
    recentActivity: [
      { id: 1, activity: 'Completed lesson: Advanced React Hooks', points: 50, date: '2023-11-10' },
      { id: 2, activity: 'Quiz score: 90%', points: 45, date: '2023-11-09' },
      { id: 3, activity: 'Discussion contribution', points: 15, date: '2023-11-09' },
      { id: 4, activity: 'Helped a peer', points: 25, date: '2023-11-08' },
      { id: 5, activity: 'Daily login bonus', points: 10, date: '2023-11-08' },
    ],
    pointsBreakdown: [
      { category: 'Course Completion', points: 750 },
      { category: 'Quiz Performance', points: 420 },
      { category: 'Discussion Participation', points: 280 },
      { category: 'Helping Peers', points: 175 },
      { category: 'Daily Bonuses', points: 225 },
    ]
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Game functions
  const openGameModal = (game) => {
    setSelectedGame(game);
    setGameModal(true);
  };

  const closeGameModal = () => {
    setGameModal(false);
    setSelectedGame(null);
  };

  const startGame = (gameId) => {
    // This would typically integrate with the actual game logic
    console.log(`Starting game ${gameId}`);
    closeGameModal();
    // Here you would navigate to the actual game component or update game state
  };

  return (
    <>
      <div className="bg-white container-rounded-lg shadow-md overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b">
          <h2 className="text-lg font-medium">Gamification & Learning Games</h2>
          <p className="text-sm text-gray-500 mt-1">Play games, earn XP, unlock achievements, and compete with peers</p>
        </div>

        {/* Progress Tracker Widget */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold">{userProgress.totalXP}</div>
              <div className="text-sm opacity-90">Total XP</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">Level {userProgress.currentLevel}</div>
              <div className="text-sm opacity-90">{userProgress.xpToNextLevel} XP to next level</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{userProgress.unlockedBadges}/{userProgress.totalBadges}</div>
              <div className="text-sm opacity-90">Badges Unlocked</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold flex items-center justify-center">
                <FaFire className="mr-1" />{userProgress.currentStreak}
              </div>
              <div className="text-sm opacity-90">Day Streak</div>
            </div>
          </div>
          
          {/* XP Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1">
              <span>Level {userProgress.currentLevel}</span>
              <span>Level {userProgress.nextLevel}</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-2">
              <div 
                className="bg-white h-2 rounded-full transition-all duration-300" 
                style={{ width: `${100 - (userProgress.xpToNextLevel / 10)}%` }}
              ></div>
            </div>
          </div>
        </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex -mb-px">
          <button
            onClick={() => setActiveTab('games')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'games' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaGamepad className="inline mr-2" />
            Learning Games
          </button>
          <button
            onClick={() => setActiveTab('badges')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'badges' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaMedal className="inline mr-2" />
            Achievement Badges
          </button>
          <button
            onClick={() => setActiveTab('leaderboards')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'leaderboards' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaTrophy className="inline mr-2" />
            Leaderboards
          </button>
          <button
            onClick={() => setActiveTab('streaks')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'streaks' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaFire className="inline mr-2" />
            Learning Streaks
          </button>
          <button
            onClick={() => setActiveTab('points')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'points' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaStar className="inline mr-2" />
            Points System
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Learning Games */}
        {activeTab === 'games' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Learning Game Modules</h3>
              <span className="text-sm text-gray-500">
                {learningGames.filter(g => g.completedCount > 0).length} of {learningGames.length} started
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {learningGames.map(game => (
                <div key={game.id} className={`border container-rounded-lg p-6 transition-all duration-300 hover:shadow-lg ${game.locked ? 'opacity-50' : 'hover:border-primary'}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-full bg-gray-100">
                      {game.icon}
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${game.difficultyColor}`}>
                      {game.difficulty}
                    </span>
                  </div>
                  
                  <h4 className="font-semibold text-lg mb-2">{game.title}</h4>
                  <p className="text-gray-600 text-sm mb-4">{game.description}</p>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <FaStar className="text-yellow-500 mr-1" />
                      <span className="text-sm font-medium">{game.xpReward} XP</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {game.completedCount}/{game.totalChallenges} completed
                    </div>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${(game.completedCount / game.totalChallenges) * 100}%` }}
                    ></div>
                  </div>
                  
                  <button 
                    onClick={() => game.locked ? null : openGameModal(game)}
                    disabled={game.locked}
                    className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                      game.locked 
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                        : 'bg-primary text-white hover:bg-primary-dark'
                    }`}
                  >
                    {game.locked ? (
                      'Locked'
                    ) : (
                      <div className="flex items-center justify-center">
                        <FaPlay className="mr-2" />
                        {game.completedCount > 0 ? 'Continue' : 'Start Game'}
                      </div>
                    )}
                  </button>
                </div>
              ))}
            </div>
            
            <div className="mt-8 bg-blue-50 border border-blue-200 container-rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-800 mb-2">How Learning Games Work</h4>
              <ul className="space-y-1 text-sm text-blue-700">
                <li>• Complete challenges to earn XP and unlock new levels</li>
                <li>• Each game has multiple difficulty levels and challenges</li>
                <li>• Earn badges by achieving high scores and completing streaks</li>
                <li>• Compete with classmates on the leaderboards</li>
              </ul>
            </div>
          </div>
        )}

        {/* Achievement Badges */}
        {activeTab === 'badges' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Your Achievement Badges</h3>
              <span className="text-sm text-gray-500">{badges.filter(b => b.earned).length} of {badges.length} earned</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {badges.map(badge => (
                <div key={badge.id} className={`border container-rounded-lg p-4 ${badge.earned ? 'bg-white' : 'bg-gray-50'}`}>
                  <div className="flex items-center">
                    <div className={`p-3 rounded-full ${badge.earned ? 'bg-green-100' : 'bg-gray-200'} mr-4`}>
                      {badge.icon}
                    </div>
                    <div>
                      <h4 className="font-medium">{badge.name}</h4>
                      <p className="text-xs text-gray-500">{badge.description}</p>
                    </div>
                  </div>
                  
                  {badge.earned ? (
                    <div className="mt-3 text-xs text-green-600 flex items-center">
                      <FaCheck className="mr-1" /> Earned on {formatDate(badge.date)}
                    </div>
                  ) : (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Progress: {badge.progress}/{badge.total}</span>
                        <span>{Math.round((badge.progress / badge.total) * 100)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-primary h-2 rounded-full" 
                          style={{ width: `${(badge.progress / badge.total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Leaderboards */}
        {activeTab === 'leaderboards' && (
          <div>
            <h3 className="font-medium mb-6">Leaderboards</h3>
            
            <div className="space-y-8">
              {leaderboards.map((board, boardIndex) => (
                <div key={boardIndex}>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">{board.category}</h4>
                  <div className="bg-gray-50 container-rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {board.users.map((user) => (
                          <tr 
                            key={user.rank} 
                            className={user.isCurrentUser ? 'bg-blue-50' : 'hover:bg-gray-100'}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                {user.rank === 1 ? (
                                  <FaTrophy className="text-yellow-500 mr-2" />
                                ) : user.rank === 2 ? (
                                  <FaMedal className="text-gray-400 mr-2" />
                                ) : user.rank === 3 ? (
                                  <FaMedal className="text-yellow-700 mr-2" />
                                ) : (
                                  <span className="text-gray-500 font-medium mr-2">{user.rank}</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                                  {user.avatar ? (
                                    <img src={user.avatar} alt={user.name} className="h-8 w-8 rounded-full" />
                                  ) : (
                                    user.name.charAt(0)
                                  )}
                                </div>
                                <div className="ml-4">
                                  <div className={`text-sm font-medium ${user.isCurrentUser ? 'text-blue-600' : 'text-gray-900'}`}>
                                    {user.name}
                                    {user.isCurrentUser && <span className="text-xs text-blue-600 ml-2">(You)</span>}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full">
                                {user.points} pts
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Learning Streaks */}
        {activeTab === 'streaks' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Your Learning Streak</h3>
              <div className="flex items-center">
                <FaFire className="text-orange-500 mr-2" />
                <span className="text-2xl font-bold">{streakData.currentStreak}</span>
                <span className="text-sm text-gray-500 ml-2">days</span>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-orange-100 to-yellow-100 container-rounded-lg p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Current Streak</h4>
                  <p className="text-3xl font-bold text-orange-500">{streakData.currentStreak} days</p>
                </div>
                <div className="text-right">
                  <h4 className="text-sm font-medium text-gray-700">Longest Streak</h4>
                  <p className="text-3xl font-bold text-orange-500">{streakData.longestStreak} days</p>
                </div>
              </div>
              
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Next Milestone</h4>
                <div className="w-full bg-white rounded-full h-3">
                  <div 
                    className="bg-orange-500 h-3 rounded-full" 
                    style={{ width: `${(streakData.currentStreak / streakData.nextMilestone) * 100}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Current: {streakData.currentStreak} days</span>
                  <span>Goal: {streakData.nextMilestone} days</span>
                </div>
              </div>
            </div>
            
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">This Week</h4>
              <div className="flex justify-between">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${streakData.thisWeek[index] 
                      ? 'bg-orange-500 text-white' 
                      : index >= 5 
                        ? 'bg-gray-100 text-gray-400' 
                        : 'bg-gray-200 text-gray-500'}`}
                    >
                      {streakData.thisWeek[index] ? <FaCheck /> : day}
                    </div>
                    <span className="text-xs text-gray-500">{day}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Recent Activity</h4>
              <div className="space-y-2">
                {streakData.streakHistory.slice(0, 5).map((day, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 container-rounded-lg">
                    <span className="text-sm">{formatDate(day.date)}</span>
                    {day.completed ? (
                      <span className="text-green-500 flex items-center">
                        <FaCheck className="mr-1" /> Completed
                      </span>
                    ) : (
                      <span className="text-red-500">Missed</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Points System */}
        {activeTab === 'points' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Your Points</h3>
              <div className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full flex items-center">
                <FaStar className="text-yellow-500 mr-2" />
                <span className="font-bold">{pointsData.totalPoints}</span>
                <span className="text-xs ml-1">points</span>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-blue-100 to-purple-100 container-rounded-lg p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Current Level</h4>
                  <div className="flex items-baseline">
                    <p className="text-3xl font-bold text-blue-600">{pointsData.level}</p>
                    <p className="text-sm text-gray-500 ml-2">/ {pointsData.nextLevel}</p>
                  </div>
                </div>
                <div className="text-right">
                  <h4 className="text-sm font-medium text-gray-700">Points to Next Level</h4>
                  <p className="text-3xl font-bold text-purple-600">{pointsData.pointsToNextLevel}</p>
                </div>
              </div>
              
              <div className="mt-4">
                <div className="w-full bg-white rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full" 
                    style={{ width: `${100 - (pointsData.pointsToNextLevel / 10)}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Level {pointsData.level}</span>
                  <span>Level {pointsData.nextLevel}</span>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Recent Activity</h4>
                <div className="space-y-2">
                  {pointsData.recentActivity.map((activity) => (
                    <div key={activity.id} className="flex items-center justify-between p-3 bg-gray-50 container-rounded-lg">
                      <div>
                        <p className="text-sm font-medium">{activity.activity}</p>
                        <p className="text-xs text-gray-500">{formatDate(activity.date)}</p>
                      </div>
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                        +{activity.points} pts
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Points Breakdown</h4>
                <div className="bg-gray-50 container-rounded-lg p-4">
                  {pointsData.pointsBreakdown.map((category, index) => (
                    <div key={index} className="mb-3 last:mb-0">
                      <div className="flex justify-between text-sm mb-1">
                        <span>{category.category}</span>
                        <span className="font-medium">{category.points} pts</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full" 
                          style={{ width: `${(category.points / pointsData.totalPoints) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 border border-green-200 container-rounded-lg p-4">
              <h4 className="text-sm font-medium text-green-800 mb-2">Points Opportunities</h4>
              <ul className="space-y-2 text-sm text-green-700">
                <li className="flex items-center">
                  <FaCheck className="mr-2 text-green-500" />
                  Complete daily learning goals: +10 points
                </li>
                <li className="flex items-center">
                  <FaCheck className="mr-2 text-green-500" />
                  Participate in discussions: +15 points per quality post
                </li>
                <li className="flex items-center">
                  <FaCheck className="mr-2 text-green-500" />
                  Help other students: +25 points per helpful answer
                </li>
                <li className="flex items-center">
                  <FaCheck className="mr-2 text-green-500" />
                  Maintain a 7-day streak: +50 bonus points
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Game Modal */}
    {gameModal && selectedGame && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white container-rounded-lg max-w-md w-full mx-4">
          <div className="flex items-center justify-between p-6 border-b">
            <h3 className="text-lg font-semibold">{selectedGame.title}</h3>
            <button 
              onClick={closeGameModal}
              className="text-gray-400 hover:text-gray-600"
            >
              <FaTimes />
            </button>
          </div>
          
          <div className="p-6">
            <div className="flex items-center justify-center mb-4">
              <div className="p-4 rounded-full bg-gray-100">
                {selectedGame.icon}
              </div>
            </div>
            
            <p className="text-gray-600 text-center mb-6">{selectedGame.description}</p>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Difficulty:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedGame.difficultyColor}`}>
                  {selectedGame.difficulty}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">XP Reward:</span>
                <span className="text-sm font-medium flex items-center">
                  <FaStar className="text-yellow-500 mr-1" />
                  {selectedGame.xpReward} XP
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Progress:</span>
                <span className="text-sm font-medium">
                  {selectedGame.completedCount}/{selectedGame.totalChallenges} challenges
                </span>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button 
                onClick={closeGameModal}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={() => startGame(selectedGame.id)}
                className="flex-1 py-2 px-4 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center justify-center"
              >
                <FaPlay className="mr-2" />
                Start Game
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Gamification;